import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  ExternalLink, 
  Clock, 
  CheckCircle, 
  XCircle, 
  RefreshCw,
  Calendar
} from 'lucide-react';
import config from '../config';

const API_URL = config.API_URL;

const XmlFeedsManager = ({ token }) => {
  const [feeds, setFeeds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedAccount, setSelectedAccount] = useState('');

  // Récupérer l'accountId depuis l'URL
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const accountParam = urlParams.get('account');
    if (accountParam) {
      setSelectedAccount(accountParam);
    }
  }, []);

  // Charger les flux
  useEffect(() => {
    loadFeeds();
  }, [token]);

  const loadFeeds = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/tools/product-labeling/feeds`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setFeeds(response.data.feeds);
      }
    } catch (err) {
      setError('Erreur lors du chargement des flux XML');
      console.error('Erreur:', err);
    } finally {
      setLoading(false);
    }
  };


  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('fr-FR');
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'COMPLETED':
        return <CheckCircle size={16} className="text-green-500" />;
      case 'FAILED':
        return <XCircle size={16} className="text-red-500" />;
      case 'RUNNING':
        return <RefreshCw size={16} className="text-blue-500 animate-spin" />;
      default:
        return <Clock size={16} className="text-gray-500" />;
    }
  };

  const getCategoryStats = (productsStats) => {
    if (!productsStats) return null;
    
    const stats = typeof productsStats === 'string' ? JSON.parse(productsStats) : productsStats;
    return (
      <div className="text-sm text-gray-600">
        TOP: {stats.TOP || 0} | MIDDLE: {stats.MIDDLE || 0} | LOW: {stats.LOW || 0} | 
        ZERO: {stats.ZERO || 0} | ZOMBIES: {stats.ZOMBIE || 0}
      </div>
    );
  };

  return (
    <div className="xml-feeds-manager">
      <div className="max-w-6xl mx-auto p-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Gestion des flux XML</h1>
          <p className="text-gray-600">
            Votre flux XML est automatiquement mis à jour chaque jour à l'heure que vous configurez.
          </p>
        </div>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        {/* URL du flux XML */}
        <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="font-medium text-blue-900 mb-2">URL de votre flux XML</h3>
          <p className="text-blue-700 text-sm mb-3">
            Utilisez cette URL dans Google Merchant Center pour importer automatiquement vos custom_label :
          </p>
          {selectedAccount ? (
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={`${API_URL}/api/tools/product-labeling/account-feed/${selectedAccount}`}
                readOnly
                className="flex-1 px-3 py-2 text-sm border border-blue-300 rounded bg-white"
              />
              <button
                onClick={() => navigator.clipboard.writeText(`${API_URL}/api/tools/product-labeling/account-feed/${selectedAccount}`)}
                className="px-3 py-2 bg-blue-500 hover:bg-blue-600 text-white text-sm rounded"
              >
                Copier
              </button>
              <button
                onClick={() => window.open(`${API_URL}/api/tools/product-labeling/account-feed/${selectedAccount}`, '_blank')}
                className="p-2 text-blue-500 hover:text-blue-700"
                title="Ouvrir le flux"
              >
                <ExternalLink size={16} />
              </button>
            </div>
          ) : (
            <p className="text-blue-600 text-sm">Aucun compte sélectionné</p>
          )}
        </div>

        {/* Liste des flux */}
        {loading ? (
          <div className="text-center py-8">
            <RefreshCw className="animate-spin mx-auto mb-2" size={24} />
            Chargement des flux...
          </div>
        ) : feeds.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Calendar size={48} className="mx-auto mb-4 text-gray-300" />
            Aucun flux XML disponible
          </div>
        ) : (
          <div className="space-y-4">
            {feeds.map((feed) => (
              <div key={feed.id} className="bg-white border border-gray-200 rounded-lg p-6">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">{feed.name}</h3>
                    <p className="text-sm text-gray-500">
                      Créé le {formatDate(feed.createdAt)}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-1 rounded-full text-xs ${
                      feed.isActive 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {feed.isActive ? 'Actif' : 'Inactif'}
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <h4 className="font-medium text-gray-700 mb-2">Paramètres de mise à jour</h4>
                    <div className="text-sm text-gray-600">
                      <div className="flex items-center gap-2 mb-1">
                        <Clock size={14} />
                        Fréquence: Chaque jour à {feed.updateTime || '04:00'}
                      </div>
                      <div>
                        Prochaine mise à jour: {feed.nextUpdate ? formatDate(feed.nextUpdate) : 'Non programmée'}
                      </div>
                      <div>
                        Dernière mise à jour: {feed.lastUpdated ? formatDate(feed.lastUpdated) : 'Jamais'}
                      </div>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-medium text-gray-700 mb-2">URL du flux</h4>
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={feed.url}
                        readOnly
                        className="flex-1 px-3 py-1 text-sm border border-gray-300 rounded bg-gray-50"
                      />
                      <button
                        onClick={() => window.open(feed.url, '_blank')}
                        className="p-1 text-blue-500 hover:text-blue-700"
                        title="Ouvrir le flux"
                      >
                        <ExternalLink size={16} />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Journal des mises à jour */}
                <div>
                  <h4 className="font-medium text-gray-700 mb-2">Journal des mises à jour (10 dernières)</h4>
                  {feed.updateLogs && feed.updateLogs.length > 0 ? (
                    <div className="space-y-2">
                      {feed.updateLogs.map((log) => (
                        <div key={log.id} className="bg-gray-50 p-3 rounded border">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              {getStatusIcon(log.status)}
                              <span className="font-medium text-sm">
                                {log.status === 'COMPLETED' ? 'Succès' : 
                                 log.status === 'FAILED' ? 'Échec' : 'En cours'}
                              </span>
                              <span className="text-sm text-gray-500">
                                {formatDate(log.startedAt)}
                              </span>
                            </div>
                            <span className="text-sm text-gray-600">
                              {log.productsCount} produits
                            </span>
                          </div>
                          
                          {log.status === 'COMPLETED' && getCategoryStats(log.productsStats)}
                          
                          {log.status === 'FAILED' && log.errorMessage && (
                            <div className="text-red-600 text-sm mt-1">
                              Erreur: {log.errorMessage}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-sm">Aucune mise à jour effectuée</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default XmlFeedsManager;